import {
  DEFAULT_REPORT_STYLE,
  ReportStyle,
  SectionedReport,
  SectionedReportSection
} from '@/types/pulse';

// Numeric enums carry their names as reverse-mapped keys too, so the values
// have to be narrowed to the numbers to make a membership test of them.
const STYLES = new Set<number>(
  Object.values(ReportStyle).filter(
    (v): v is ReportStyle => typeof v === 'number'
  )
);

/**
 * Read a report style from untrusted input — a stored report written before
 * styles existed, a value this build does not know, or a form field. Anything
 * unrecognized reads as the default rather than throwing.
 *
 * Numeric strings are read as the style they spell: a `<select>` hands back the
 * option's value as text, and that is the same choice the number stands for.
 */
export function toReportStyle(value: unknown): ReportStyle {
  const numeric =
    typeof value === 'string' && value !== '' ? Number(value) : value;
  return typeof numeric === 'number' && STYLES.has(numeric)
    ? (numeric as ReportStyle)
    : DEFAULT_REPORT_STYLE;
}

/**
 * Read a stored report body. Every field the report carries has to be named
 * here: callers rebuild the stored JSON from what this returns, so a field it
 * drops is a field the next write deletes.
 */
export function parseSectionedReport(data: unknown): SectionedReport | null {
  if (!data || typeof data !== 'object') return null;
  const { executive_summary, sections, style } = data as {
    executive_summary?: unknown;
    sections?: unknown;
    style?: unknown;
  };
  if (!Array.isArray(sections)) return null;
  return {
    executive_summary:
      typeof executive_summary === 'string' ? executive_summary : '',
    sections: sections as SectionedReportSection[],
    style: toReportStyle(style)
  };
}
